/**
 * Secure API Client for Office Arena
 */

import { logger } from '../../utils/logger';
import { API_ENDPOINTS } from '../../constants/apiConstants';
import { ENV_KEYS } from '../../constants/uiConstants';

class SecureApiClient {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE_URL;
    this.timeout = 10000; // 10 second timeout
    this.isLocalStack = process.env[ENV_KEYS.USE_LOCALSTACK] === 'true';
    this.getAccessToken = null; // Will be set by Auth0 hook
    this.userContext = null; // Will be set with current user info
    this.discoveredApiUrl = null; // Cache for discovered API Gateway URL
  }

  /**
   * Set the token getter function (called by Auth0 hook)
   */
  setTokenGetter(getAccessTokenSilently) {
    this.getAccessToken = getAccessTokenSilently;
  }

  /**
   * Set the current user context (for custom headers in LocalStack)
   */
  setUserContext(user) {
    this.userContext = user;
  }

  /**
   * Get authentication token from Auth0 or create LocalStack token
   */
  async getAuthToken() {
    // Priority 1: Try to get real Auth0 token (works in both production and LocalStack)
    if (this.getAccessToken) {
      try {
        const token = await this.getAccessToken();
        if (token) {
          logger.debug('Using real Auth0 token');
          return token;
        }
      } catch (error) {
        logger.debug('Auth0 token not available:', error.message);
        // Fall through to LocalStack mock token
      }
    }

    // Priority 2: For LocalStack development, create a mock token
    if (this.isLocalStack) {
      const mockToken = this.createLocalStackAuthToken();
      logger.debug('Using LocalStack mock auth token');
      return mockToken;
    }

    // Priority 3: No auth available
    logger.debug('No token getter available, skipping auth');
    return null;
  }

  /**
   * Create a mock authentication token for LocalStack development
   */
  createLocalStackAuthToken() {
    // Create a simple token for LocalStack development only
    // WARNING: This is for development only, not for production use
    if (process.env.NODE_ENV === 'production') {
      throw new Error('Mock tokens not allowed in production');
    }

    const mockClaims = {
      sub: process.env.LOCALSTACK_ADMIN_USER || 'localstack-admin-user-123',
      email: process.env.LOCALSTACK_ADMIN_EMAIL || 'admin@office-arena.local',
      name: 'LocalStack Admin',
      roles: ['admin'],
      iss: 'localstack-auth',
      aud: 'localstack-client',
      exp: Math.floor(Date.now() / 1000) + 3600,
    };

    const tokenPayload = btoa(JSON.stringify(mockClaims));
    return `localstack.${tokenPayload}.signature`;
  }



  /**
   * Make authenticated HTTP request
   */
  async makeRequest(endpoint, options = {}) {
    const {
      method = 'GET',
      body = null,
      headers = {},
      requireAuth = true,
    } = options;

    try {
      const requestHeaders = {
        'Content-Type': 'application/json',
        ...headers,
      };

      if (requireAuth) {
        const token = await this.getAuthToken();
        if (token) {
          requestHeaders['Authorization'] = `Bearer ${token}`;
        }

        // For LocalStack with Auth0: Add custom headers with user info
        // This works around Auth0's encrypted JWE tokens that can't be decoded server-side
        if (this.isLocalStack && this.userContext) {
          requestHeaders['X-User-Id'] = this.userContext.sub || this.userContext.id;
          requestHeaders['X-User-Email'] = this.userContext.email;
          requestHeaders['X-User-Name'] = this.userContext.name || this.userContext.email?.split('@')[0];
          logger.debug('Added custom user headers for LocalStack:', {
            id: requestHeaders['X-User-Id'],
            email: requestHeaders['X-User-Email'],
            name: requestHeaders['X-User-Name']
          });
        }
      }

      const requestConfig = {
        method,
        headers: requestHeaders,
        timeout: this.timeout,
      };

      if (body && method !== 'GET') {
        requestConfig.body = JSON.stringify(body);
      }

      const apiUrl = this.discoveredApiUrl || this.baseURL;
      logger.debug(`Making ${method} request to: ${apiUrl}${endpoint}`);
      if (this.isLocalStack) {
        logger.debug('LocalStack request headers:', requestHeaders);
      }

      const response = await fetch(`${apiUrl}${endpoint}`, requestConfig);

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(
          `API request failed: ${response.status} ${response.statusText}`,
          errorText
        );

        let errorData = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText };
        }

        // Provide more specific error messages for common LocalStack issues
        if (this.isLocalStack && response.status === 403) {
          throw new Error(
            'LocalStack API Gateway authorization failed - check API Gateway configuration'
          );
        }

        if (response.status === 400) {
          logger.error('400 Bad Request details:', errorData);
          throw new Error(
            errorData.message || `Bad Request: ${errorText || 'Invalid request format'}`
          );
        }

        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();
      logger.debug(`${method} request successful:`, data);

      return data;
    } catch (error) {
      logger.error(`API request failed for ${endpoint}:`, error);

      // Handle specific error types
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please try again');
      }

      if (
        error.message.includes('NetworkError') ||
        error.message.includes('Failed to fetch')
      ) {
        if (this.isLocalStack) {
          throw new Error(
            'LocalStack connection failed - is LocalStack running on localhost:4566?'
          );
        }
        throw new Error('Network error - please check your connection');
      }

      throw error;
    }
  }

  // Player API methods
  async getPlayers() {
    logger.debug('apiClient.getPlayers() called');
    const response = await this.makeRequest(API_ENDPOINTS.PLAYERS.LIST);
    logger.debug('getPlayers response:', response);
    return response.players || [];
  }

  async getMyProfile() {
    logger.debug('apiClient.getMyProfile() called');
    const response = await this.makeRequest(API_ENDPOINTS.PLAYERS.ME);
    logger.debug('getMyProfile response:', response);
    return response.player || null;
  }

  async getPlayer(playerId) {
    const response = await this.makeRequest(
      API_ENDPOINTS.PLAYERS.GET.replace(':id', playerId)
    );
    return response;
  }

  async createPlayer(playerData) {
    logger.debug('createPlayer called with:', playerData);
    const result = await this.makeRequest(API_ENDPOINTS.PLAYERS.CREATE, {
      method: 'POST',
      body: playerData,
    });
    logger.debug('createPlayer result:', result);
    return result;
  }

  async updatePlayer(playerId, updateData) {
    return await this.makeRequest(
      API_ENDPOINTS.PLAYERS.UPDATE.replace(':id', playerId),
      {
        method: 'PUT',
        body: updateData,
      }
    );
  }

  // Match API methods
  async getMatches(limit = null) {
    const endpoint = limit
      ? `${API_ENDPOINTS.MATCHES.LIST}?limit=${limit}`
      : API_ENDPOINTS.MATCHES.LIST;
    const response = await this.makeRequest(endpoint);
    return response.matches || [];
  }

  async getMatch(matchId) {
    const response = await this.makeRequest(
      API_ENDPOINTS.MATCHES.GET.replace(':id', matchId)
    );
    return response;
  }

  async createMatch(matchData) {
    const response = await this.makeRequest(API_ENDPOINTS.MATCHES.CREATE, {
      method: 'POST',
      body: matchData,
    });
    return response;
  }

  async createMatches(matchesData) {
    const response = await this.makeRequest(API_ENDPOINTS.MATCHES.BATCH_CREATE, {
      method: 'POST',
      body: { matches: matchesData },
    });
    return response;
  }

  async verifyMatch(matchId, playerId) {
    return await this.makeRequest(
      API_ENDPOINTS.MATCHES.VERIFY.replace(':id', matchId),
      {
        method: 'PUT',
        body: { action: 'verify', playerId },
      }
    );
  }

  // Bread API methods
  async getBreadLogs(limit = null) {
    const endpoint = limit
      ? `${API_ENDPOINTS.BREAD.LIST}?limit=${limit}`
      : API_ENDPOINTS.BREAD.LIST;
    const response = await this.makeRequest(endpoint);
    return response.logs || [];
  }

  async getBreadLogsByConsumer(consumerId) {
    const endpoint = `${API_ENDPOINTS.BREAD.LIST}?consumerId=${consumerId}`;
    const response = await this.makeRequest(endpoint);
    return response.logs || [];
  }

  async createBreadLog(breadData) {
    return await this.makeRequest(API_ENDPOINTS.BREAD.CREATE, {
      method: 'POST',
      body: breadData,
    });
  }

  async updateBreadLog(logId, updateData) {
    return await this.makeRequest(
      API_ENDPOINTS.BREAD.UPDATE.replace(':id', logId),
      {
        method: 'PUT',
        body: updateData,
      }
    );
  }

  async calculateBreadDebt() {
    const response = await this.makeRequest(API_ENDPOINTS.BREAD.DEBT);
    return response;
  }

  // Chemistry methods
  async getChemistryWeights() {
    return await this.makeRequest(API_ENDPOINTS.CHEMISTRY.GET_WEIGHTS, {
      requireAuth: false,
    });
  }

  async updateChemistryWeights(weights) {
    return await this.makeRequest(API_ENDPOINTS.CHEMISTRY.UPDATE_WEIGHTS, {
      method: 'PUT',
      body: weights,
    });
  }

  // Health check method
  async healthCheck() {
    try {
      await this.makeRequest('/health', { requireAuth: false });
      return true;
    } catch (error) {
      logger.error('API health check failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const apiClient = new SecureApiClient();

// Export class for testing
export { SecureApiClient };
